import React, { useContext, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectSearch } from '../../store/selectors/search';
import { selectAuthenticatedUser } from '../../store/selectors/auth';
import { selectLoader } from '../../store/selectors/loader';
import Toast from '../Toast';
import { toast } from 'react-toastify';
import TextareaAutosize from 'react-textarea-autosize';
import Icon from '../Icon';
import Button from '../Button';
import MessagesRepeater from './MessagesRepeater';
import Loader from '../Loader';
import Empty from '../Empty';
import {
  BrandingContext,
  useBranding,
} from '../../branding/BrandingContextProvider';
import { checkMediaType } from '../../helpers/CommonHelpers';
import Attachment from '../Attachment';
import './Messages.scss';
import 'react-toastify/dist/ReactToastify.css';
import 'react-image-lightbox/style.css';
import { MessageItem } from 'src/components/Messages/types';
import ReusableLightbox from 'src/components/ReusableLightbox/reusable-lightbox';

// Array of allowed media types.
const ALLOWED_MEDIA_TYPES = ['.jpg', '.jpeg', '.png', '.pdf'];

interface MessageProps {
  messages: MessageItem[];
  serviceRequestId: string;
  onSendEmailNotification: (messageId: string) => void;
  onPostMessage: (data: any) => Promise<void>;
  ticketId: string;
  url: string;
}

export const Messages = ({
  messages,
  onSendEmailNotification,
  onPostMessage,
}: MessageProps) => {
  // Access branding configuration from context provider.
  const { branding } = useBranding(BrandingContext);

  // Manage component-specific state values including file attachments and message content.
  const [messageRowState, setMessageRowState] = useState<any>({
    selectedFile: [],
    isOpen: false,
    selectedIndex: 0,
    note: {},
    sentMessage: false,
    theme: localStorage.getItem('theme'),
    showOverlay: false,
    message: '',
  });

  // Retrieve user's theme preference from browser storage.
  const theme = localStorage.getItem('theme');

  // Connect to Redux store data via selector hooks.
  const search = useSelector(selectSearch);
  const user = useSelector(selectAuthenticatedUser);
  const loader = useSelector(selectLoader);

  // Validation, preparation and submission of new messages to the backend.
  const postMessage = async () => {
    // Error handling for empty messages.
    if (messageRowState.message.trim().length === 0) {
      setMessageRowState((prev) => ({
        ...prev,
        sentMessage: false,
      }));

      return toast.error('🚫 Please enter your message before submitting.', {
        autoClose: 12000,
        position: 'top-center',
      });
    }

    // Create data object with current message.
    const data: any = {
      message: messageRowState.message,
    };

    if (
      messageRowState.selectedFile &&
      messageRowState.selectedFile.length > 0
    ) {
      data.image = messageRowState.selectedFile;
    }

    try {
      // This callback goes to parent > redux > API.
      await onPostMessage(data);

      // Update state after successful API call.
      setMessageRowState((prev) => ({
        ...prev,
        sentMessage: true,
        message: '',
        selectedFile: [],
      }));

      console.log('Message sent successfully');
    } catch (e) {
      console.error('Error posting message:', e);
      toast.error('There was a problem sending your message.', {
        autoClose: 5000,
        position: 'top-center',
      });
    }
  };

  // Extract file extension from URL for type checking.
  const get_url_extension = (url) => {
    let ext = url.split(/[#?]/)[0].split('.').pop().trim();
    return ext.toLowerCase();
  };

  // Process and validate uploaded file attachments.
  const handleFileSelection = (file) => {
    if (checkMediaType(file[0].path, ALLOWED_MEDIA_TYPES)) {
      setMessageRowState((prev) => ({
        ...prev,
        selectedFile: file,
      }));
      return;
    }

    toast.error(
      '🚫 You have uploaded an invalid file format, allowed formats are JPG, PNG, or PDF only.',
      { autoClose: 5000, position: 'top-center' }
    );
  };

  // Reset file attachment selection.
  const handleClearFiles = () => {
    setMessageRowState((prev) => ({ ...prev, selectedFile: [] }));
  };

  // Handle viewing of attached images and documents.
  const handleImageClick = (index, note) => {
    if (
      get_url_extension(note.fileURL) === 'pdf' ||
      get_url_extension(note.fileURL) === 'heic'
    ) {
      window.open(note.fileURL);
    } else {
      setMessageRowState((prev) => ({
        ...prev,
        isOpen: true,
        selectedIndex: index,
        note: note,
      }));
    }
  };

  // Close the lightbox image viewer.
  const closeLightbox = () => {
    setMessageRowState((prev) => ({
      ...prev,
      isOpen: false,
    }));
  };

  return (
    <div className="messages">
      {/* Notification toast with service level expectations. */}
      {theme === 'Formwerks' && (
        <div className="messages__messages" style={{ marginTop: 10 }}>
          <Toast
            color="teal"
            message={
              'Please note: Our team will get back to you within 3 business days. Our business hours are Monday to Friday from 9:00 am to 5:00 pm, excluding statutory holidays. If your request is an emergency, please contact your property manager.'
            }
            actions={undefined}
          />
        </div>
      )}
      {/* Main container. */}
      {user.roles[0].role.name !== 'Realtor' ? (
        <>
          {/* Message composition area. */}
          <div className="messages__input">
            <div className="mb-10">
              <TextareaAutosize
                className="autoincreasetextarea Sroll"
                style={{ width: '100%' }}
                minRows={2}
                maxRows={6}
                placeholder="Enter Your Message Here!"
                value={messageRowState.message || ''}
                onChange={(e) => {
                  const newValue = e.target.value;
                  console.log('New input value:', newValue);
                  setMessageRowState((prev) => ({
                    ...prev,
                    message: newValue,
                  }));
                }}
              />
            </div>
          </div>
          {/* Action buttons for submitting messages and attaching files. */}
          <div className="messages__actions">
            <div className="messages__enemies">
              <Button
                color="teal"
                label="Post Message"
                onClick={postMessage}
                disabled={!messageRowState?.message?.trim()}
                icon={<Icon glyph="edit" weight="light" size="small" />}
                size="small"
                block={undefined}
                outlined={undefined}
                outline={undefined}
                weight={undefined}
                children={undefined}
                style={undefined}
              />
              <Attachment
                onSelect={handleFileSelection}
                files={
                  messageRowState.selectedFile &&
                  messageRowState.selectedFile.length > 0
                    ? messageRowState.selectedFile
                    : []
                }
                clearFiles={handleClearFiles}
                icon={undefined}
                isMultiple={undefined}
              />
            </div>
          </div>
        </>
      ) : null}
      {/* Message history display area. */}
      {messages.length > 0 ? (
        <>
          {theme !== 'Formwerks' && messages.length === 1
            ? messageRowState.sentMessage && (
                <div className="messages__messages">
                  <Toast
                    color="teal"
                    message={branding?.text?.messageConfirmationBody ?? ''}
                    actions={undefined}
                  />
                </div>
              )
            : null}
          {loader === false ? (
            <MessagesRepeater
              notes={messages}
              onSendEmailNotification={onSendEmailNotification}
              user={user}
              onImageClicked={handleImageClick}
            />
          ) : (
            <Loader />
          )}
          {/* Image lightbox for viewing full-size attachments. */}
          {messageRowState.isOpen ? (
            <ReusableLightbox
              isOpen={messageRowState.isOpen}
              onClose={closeLightbox}
              image={messageRowState.note.fileURL}
            />
          ) : null}
        </>
      ) : (
        // Empty state when no messages exist.
        <Empty
          headline={branding?.text?.noMessages}
          strapline={`"You can use the message section to ask questions, have a conversation" \n ${
            localStorage.getItem('theme') === 'Rize'
              ? ''
              : ',or connect directly via a live video call within Juniper.'
          }.`}
          searchIsActive={search && search.messages ? search.messages : ''}
          image={<img alt="No notes yet" src="/assets/empty/notes.svg" />}
          cta={undefined}
          direction={undefined}
        />
      )}
    </div>
  );
};
